import { NextPage } from 'next';
import Image from 'next/image';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import MapPin from '@/assets/about/footer/MapPin.svg';
import MessageCircle from '@/assets/about/footer/MessageCircle.svg';
import Star from '@/assets/about/footer/Star.svg';
import GalleryImage1 from '@/assets/about/gallery/place-losangeles@2x.jpg';
import GalleryImage2 from '@/assets/about/gallery/place-losangeles2@2x.jpg';
import GalleryImage3 from '@/assets/about/gallery/place-losangeles3@2x.jpg';
import GalleryImage4 from '@/assets/about/gallery/place-losangeles4@2x.jpg';
import Book from '@/assets/about/solution/Book.svg';
import CamperVan from '@/assets/about/solution/CamperVan.svg';
import FileText from '@/assets/about/solution/FileText.svg';
import ExperienceInfo from '@/components/about/experience-info';
import SolutionButton from '@/components/about/solution-button';
import Button, { ButtonVariants } from '@/components/switchback/Button/Button';

import css from './index.module.css';

const About: NextPage = () => {
  const intl = useIntl();
  return (
    <div className={css.mainWrapper}>
      {/* Header */}
      <div className={css.headerWrapper}>
        <video className={css.video} autoPlay playsInline muted loop>
          <source
            src={'https://d1o5877uy6tsnd.cloudfront.net/static-assets/videos/header-video.mp4'}
            type="video/mp4"
          />
        </video>
        <div className={css.headerContent}>
          <div className={css.headerTitle}>
            <i>
              <FormattedMessage defaultMessage="Passionate" id="srtcU5" />
            </i>
            <span>
              {' '}
              <FormattedMessage defaultMessage="People" id="Tpy00S" />
            </span>
          </div>
          <h1 className={css.headerSubtitle}>
            <FormattedMessage defaultMessage="Unlocking the Outdoors" id="tAUmzl" />
          </h1>
        </div>
      </div>
      {/* Problem */}
      <div className={`${css.section} ${css.problemWrapper}`}>
        <div className={css.problemTitle}>
          <FormattedMessage defaultMessage="WE SAW A PROBLEM" id="MfT4gL" />
        </div>
        <div className={`${css.mainTitle} ${css.problemMainTitle}`}>
          <FormattedMessage
            defaultMessage="Over 17 million RVs in North America sit unused 350 days a year."
            id="X3ZQFV"
          />
        </div>
        <div className={css.problemContent}>
          <p>
            <FormattedMessage
              defaultMessage="Everyone should have the access, choice and opportunity to get outdoors. With over a billion days of available RVs inventory, we realized we could make a difference in helping people access the world outside."
              id="nuj/xL"
            />
          </p>
          <p>
            <FormattedMessage
              defaultMessage="We don’t own a fleet of identical, anonymous RVs that have seen millions of miles. We’re a peer-to-peer marketplace. We connect RV owners with other campers like them who want the experience RVing without ownership."
              id="H/m1RV"
            />
          </p>
        </div>
      </div>
      {/* Image gallery */}
      <div className={`${css.section} ${css.imageGalleryWrapper}`}>
        <div className={css.topImage}>
          <Image
            className="rounded-lg"
            width={380}
            height={280}
            alt={'GalleryImage1'}
            src={GalleryImage1}
            layout="fixed"
          />
        </div>

        <div className={css.bottomImage}>
          <Image
            className="rounded-lg"
            width={380}
            height={280}
            alt={'GalleryImage2'}
            src={GalleryImage2}
            layout="fixed"
          />
        </div>

        <div className={css.topImage}>
          <Image
            className="rounded-lg"
            width={380}
            height={280}
            alt={'GalleryImage4'}
            src={GalleryImage4}
            layout="fixed"
          />
        </div>

        <div className={css.bottomImage}>
          <Image
            className="rounded-lg"
            width={380}
            height={280}
            alt={'GalleryImage3'}
            src={GalleryImage3}
            layout="fixed"
          />
        </div>
      </div>
      {/* Solution */}
      <div className={`${css.section} ${css.solutionWrapper}`}>
        <div className={css.solutionContent}>
          <div className={css.solutionTitle}>
            <FormattedMessage defaultMessage="WE CAME UP WITH A SOLUTION" id="7bi2cX" />
          </div>
          <div className={css.mainTitle}>
            <FormattedMessage
              defaultMessage="An RV marketplace for consumers and pros."
              id="aZdiNB"
            />
          </div>
          <div className={css.solutionText}>
            <p>
              <FormattedMessage
                defaultMessage="Lots of folks own RVs. Lots of folks want to rent RVs. Outdoorsy is how they meet."
                id="/O23SF"
              />
            </p>
            <p>
              <FormattedMessage
                defaultMessage="We’re the most comprehensive platform for outdoor travelers to rent stylish awesome RVs in the nation. Our selection spans vintage Airstreams, toy haulers, fifth wheelers, Class A, B, and C of RVs, as well as garden variety trailers and motorhomes."
                id="mkbpPu"
              />
            </p>
            <p>
              <FormattedMessage
                defaultMessage="For professional RV rental companies, we launched Wheelbase for inventory management, utilization rates, and the best insurance in the business."
                id="PAUDGt"
              />
            </p>
            <p>
              <FormattedMessage
                defaultMessage="Outdoorsy is safe, easy, fully insured, and has a great local selection. We’re free to join and have no membership fees of any kind. Discover amazing RVs to travel in - or rent out your own!"
                id="RLtL45"
              />
            </p>
          </div>
        </div>
        <div className={css.solutionButtons}>
          <SolutionButton
            icon={<Book />}
            link={'/blog/outdoorsy-founder-story'}
            title={<FormattedMessage defaultMessage="Our Founder’s Story" id="ijpU26" />}
            description={
              <FormattedMessage
                defaultMessage="The road has been a long one. Read about how Outdoorsy started."
                id="z2KFkz"
              />
            }
          />
          <SolutionButton
            icon={<FileText />}
            link={'/press'}
            title={<FormattedMessage defaultMessage="Press Room" id="BfwuRi" />}
            description={
              <FormattedMessage
                defaultMessage="Download our media kit, read our company announcements and our press mentions."
                id="g58feG"
              />
            }
          />
          <SolutionButton
            icon={<CamperVan />}
            link={'/blog'}
            title={<FormattedMessage defaultMessage="Never Idle Journal" id="sBxJ6A" />}
            description={
              <FormattedMessage
                defaultMessage="Owner stories, tips and tricks, how-to guides and more!"
                id="eLf+De"
              />
            }
          />
        </div>
      </div>
      {/* Video */}
      <div className={`${css.section} ${css.videoWrapper}`}>
        <div className={css.iframeVideo}>
          <iframe
            className={css.iframe}
            title={'Brand Anthem'}
            src={`https://www.youtube.com/embed/62LP7kbASfA?enablejsapi=1&output=embed`}
            frameBorder={0}
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
        <div className={css.videoContent}>
          <div className={css.videoContentTitle}>
            <FormattedMessage defaultMessage="WATCH" id="JyE//3" />
          </div>
          <div className={css.mainTitle}>
            <FormattedMessage defaultMessage="Brand Anthem" id="EJIc8k" />
          </div>
          <div className={css.mainSubtitle}>
            <FormattedMessage
              defaultMessage="We are explorers, wanderers and nomads. Our life is on the road and there is so much to be discovered. Join us!"
              id="NLWcuD"
            />
          </div>
        </div>
      </div>
      {/* Team */}
      <div className={`${css.section} ${css.teamWrapper}`}>
        <div>
          <div className={css.mainTitle}>
            <FormattedMessage defaultMessage="Want to join our team?" id="NwLsJU" />
          </div>
          <div className={css.mainSubtitle}>
            <FormattedMessage defaultMessage="Checkout our careers page" id="xTchcK" />
          </div>
          <Button
            className={css.careerButton}
            href={'/jobs'}
            variant={ButtonVariants.primaryOutlined}>
            <FormattedMessage defaultMessage="Careers at Outdoorsy" id="PKpvfX" />
          </Button>
        </div>
      </div>
      {/* Footer */}
      <div className={`${css.section} ${css.footerWrapper}`}>
        <div>
          <div className={css.mainTitle}>
            <FormattedMessage defaultMessage="Thousands of Happy RV Campers" id="TsWSJn" />
          </div>
          <div className={css.mainSubtitle}>
            <FormattedMessage
              defaultMessage="Our RV renters & owners enthusiastically rave about the Outdoorsy experience"
              id="X5HcoF"
            />
          </div>
        </div>
        <div className={css.experienceInfoWrapper}>
          <ExperienceInfo
            icon={<Star />}
            title={<FormattedMessage defaultMessage="5000+ Reviews" id="5nr5/G" />}
            description={intl.formatMessage({
              defaultMessage:
                'Our customers love us! We have over 5,000+ reviews with a 4.87 average rating.',
              id: 'tPxV4f',
            })}
          />
          <ExperienceInfo
            icon={<MessageCircle />}
            title={<FormattedMessage defaultMessage="1.2M+ Interactions" id="WGlsPh" />}
            description={intl.formatMessage({
              defaultMessage:
                'It all starts with a conversation. We’ve helped connect owners and renters everywhere!',
              id: '4QHNso',
            })}
          />
          <ExperienceInfo
            icon={<MapPin />}
            title={<FormattedMessage defaultMessage="240K Trips Taken" id="dgvs4X" />}
            description={intl.formatMessage({
              defaultMessage:
                'Thousands of people have used Outdoorsy to find their perfect RV rental. You can too!',
              id: 'PIMCTa',
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default About;
